/**
 * Camera Interpolation Utilities
 * Interpolate camera position/rotation untuk camera animation system
 */

/**
 * Interpolate camera at specific time
 * @param {Array} keyframes - Camera keyframes
 * @param {number} currentTime - Current time in animation
 * @param {string} interpolationType - 'linear', 'smooth', atau 'cubic'
 * @param {Object} camera - Three.js camera object
 * @param {Object} controls - OrbitControls object
 * @param {Object} THREE - Three.js module
 */
export function interpolateCameraAtTime(
  keyframes,
  currentTime,
  interpolationType,
  camera,
  controls,
  THREE
) {
  if (keyframes.length < 2 || !camera || !controls || !THREE) {
    return
  }

  // Sort keyframes by time
  const sortedKeyframes = [...keyframes].sort((a, b) => a.time - b.time)

  // Find surrounding keyframes
  let prevKf = sortedKeyframes[0]
  let nextKf = sortedKeyframes[sortedKeyframes.length - 1]

  // If current time is before first keyframe
  if (currentTime <= prevKf.time) {
    prevKf = sortedKeyframes[0]
    nextKf = sortedKeyframes[0]
  }
  // If current time is after last keyframe
  else if (currentTime >= nextKf.time) {
    prevKf = sortedKeyframes[sortedKeyframes.length - 1]
    nextKf = sortedKeyframes[sortedKeyframes.length - 1]
  }
  // Find the two keyframes that surround current time
  else {
    for (let i = 0; i < sortedKeyframes.length - 1; i++) {
      if (
        currentTime >= sortedKeyframes[i].time &&
        currentTime <= sortedKeyframes[i + 1].time
      ) {
        prevKf = sortedKeyframes[i]
        nextKf = sortedKeyframes[i + 1]
        break
      }
    }
  }

  // Calculate interpolation factor (t)
  let t = 0
  if (nextKf.time !== prevKf.time) {
    t = (currentTime - prevKf.time) / (nextKf.time - prevKf.time)
  }
  const clampedT = Math.max(0, Math.min(1, t))

  // Apply easing based on interpolation type
  let easedT = clampedT
  if (interpolationType === 'smooth') {
    easedT = clampedT * clampedT * (3 - 2 * clampedT)
  } else if (interpolationType === 'cubic') {
    easedT =
      clampedT < 0.5
        ? 4 * clampedT * clampedT * clampedT
        : 1 - Math.pow(-2 * clampedT + 2, 3) / 2
  }

  // Interpolate position
  camera.position.x =
    prevKf.position.x + (nextKf.position.x - prevKf.position.x) * easedT
  camera.position.y =
    prevKf.position.y + (nextKf.position.y - prevKf.position.y) * easedT
  camera.position.z =
    prevKf.position.z + (nextKf.position.z - prevKf.position.z) * easedT

  // Interpolate quaternion using slerp
  const prevQuat = new THREE.Quaternion(
    prevKf.quaternion.x,
    prevKf.quaternion.y,
    prevKf.quaternion.z,
    prevKf.quaternion.w
  )
  const nextQuat = new THREE.Quaternion(
    nextKf.quaternion.x,
    nextKf.quaternion.y,
    nextKf.quaternion.z,
    nextKf.quaternion.w
  )
  camera.quaternion.slerpQuaternions(prevQuat, nextQuat, easedT)

  // Interpolate orbit controls target
  controls.target.x =
    prevKf.target.x + (nextKf.target.x - prevKf.target.x) * easedT
  controls.target.y =
    prevKf.target.y + (nextKf.target.y - prevKf.target.y) * easedT
  controls.target.z =
    prevKf.target.z + (nextKf.target.z - prevKf.target.z) * easedT

  // Interpolate FOV
  camera.fov = prevKf.fov + (nextKf.fov - prevKf.fov) * easedT
  camera.updateProjectionMatrix()

  // Update controls
  controls.update()
}

